import { Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ServiceCardInfo } from 'src/app/models/service/service-card-info';
import { Solution } from 'src/app/models/service/solution';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { ServiceCardService } from 'src/app/services/util/service-card.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Permission } from '../../models/principal/permission';
import { RouterSegments } from 'src/app/models/router-segments';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import {translate} from '@jsverse/transloco';
import { ToastrService } from 'src/app/services/util/toastr.service';
import { ServiceStatus } from 'src/app/models/service/service-status';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { Permissions } from 'src/app/models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.BLUEPRINT_READ,
    action: Permissions.BLUEPRINT_READ
  },
  {
    key: PermissionKeys.BLUEPRINT_CREATE,
    action: Permissions.BLUEPRINT_CREATE
  },
];

@Component({
    selector: 'app-byob-my-services',
    templateUrl: './byob-my-services.component.html',
    styleUrls: ['./byob-my-services.component.scss'],
    standalone: false
})
export class ByobMyServicesComponent implements OnInit {
  servicesVersionsMap = new Map<string, SolutionVersion[]>();
  sandboxInfos: ServiceCardInfo[];
  serviceInfos: any;
  viewService = false;
  viewSandbox = true;
  isSandbox: any;
  isServices: any;
  enterpriseClient: boolean;
  hasCreateSandboxPermission: boolean;
  ServiceStatus = ServiceStatus;
  isLoader: any;
  permissionsMap: { [key: string]: boolean };
  noServices = false;
  PermissionKeys = PermissionKeys;
  private destroyRef = inject(DestroyRef);
  tenant: string;
  constructor(private solutionsService: SolutionsService, private router: Router, private serviceCardService: ServiceCardService,
    private authService: AuthService, private blueprintsService: BlueprintsService, private toastrService: ToastrService,
    private permissionsService: PermissionsService
     ) {
  }

  ngOnInit(): void {
    this.tenant = this.authService.principal.provider;
    this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    if (!this.permissionsMap[PermissionKeys.BLUEPRINT_READ]) {
      this.noServices = true;
    }
    this.loadData();
    this.enterpriseClient = !this.authService.hasPermission(Permission.VIEW_ENTERPRISE_PROSPECT);
    this.hasCreateSandboxPermission = this.authService.hasPermission(Permission.CREATE_SANDBOX);
    if(this.enterpriseClient){
      this.viewService = true;
      this.viewSandbox = false;
    }
  }

  loadData(): void {
    this.isLoader = true;
    this.solutionsService.getMySolutions()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(solutions => { 
      if(solutions.length > 0) {
        solutions.forEach((item)=> {
          // this.solutionsService.getSolutionVersions(item.solutionId);
        });
        this.loadServiceCardInformation(solutions);
      } else {
        this.isLoader = false;
        this.isServices = false;
        this.isSandbox = false;
      }
    });
  }

  viewSandboxService(viewType): void{
    if(viewType === 'service'){
      this.viewService = true;
      this.viewSandbox = false;
    }else{
      this.viewService = false;
      this.viewSandbox = true;
    }
  }

  goToMyServices(): void {
    this.router.navigate(['/', this.authService.principal.provider, RouterSegments.MY_SERVICES],
      {state: {data: this.viewSandbox}});
  }

  goToComposeService(): void {
    this.router.navigate(['/', this.authService.principal.provider, RouterSegments.COMPOSE]);
  }

  goToCreateSandbox(): void {
    // this.router.navigate(['/', this.authService.principal.provider, RouterSegments.CATALOGUE]);
  }

  private getLatestVersion(card): Promise<any> {
    return new Promise((resolve) => {
      this.blueprintsService.getBlueprintsIdLatestVersion(card.blueprint.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        resolve(result);
      }, (error) => {
        resolve(error);
        this.toastrService.clear();
            setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
    });
  }

  private loadServiceCardInformation(services: Solution[]): void {
    const fewCards = `${translate('SERVICE.FEW_CARDS')}`;
    this.serviceCardService.setCards('typeOfCard', fewCards);
    if (services?.length > 0) {
      this.serviceCardService.getServiceCardInfos(services, this.servicesVersionsMap)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async cardInfos => {
        if(cardInfos.length > 0) {
        const promises = [];
        this.sandboxInfos = cardInfos.filter(cardInfo => cardInfo.blueprint.demo && cardInfo.instances?.length && cardInfo.blueprint.tenantId == this.tenant);
        this.sandboxInfos.forEach( card => {
          const latestVersion = this.getLatestVersion(card);
          promises.push(latestVersion);
        });
        await Promise.allSettled(promises).then(latestVersions => {
          latestVersions = latestVersions.map((version: any) => version.value);
          this.sandboxInfos = this.sandboxInfos.filter(cardInfo => cardInfo.blueprint.demo);
          this.sandboxInfos.forEach((card: any) => {
            card.latestVersion = latestVersions.find((version: any) => version?.blueprintId === card.blueprint?.id);
            if (!card.sandboxInfos?.devVersion) {
              if(this.servicesVersionsMap.get(card.blueprint.solutionId)) {
                card.buildNumber = this.servicesVersionsMap.get(card.blueprint.solutionId)[0]?.version;
              }
            }
          });
        });
        // const serviceInfo = cardInfos.filter(cardInfo => !cardInfo.blueprint.demo).reverse().slice(0,4);
        const filteredServices = cardInfos.filter(cardInfo => !cardInfo.blueprint.demo && cardInfo.blueprint.tenantId == this.tenant);
        const serviceInfo = filteredServices.length > 20
          ? filteredServices.sort(this.defaultComparator).slice(0, 20)
          : filteredServices.sort(this.defaultComparator);
        // const serviceInfo = cardInfos.filter(cardInfo => !cardInfo.blueprint.demo).sort(this.defaultComparator).slice(0, 4);

        this.serviceInfos = serviceInfo.filter(cardInfo => !cardInfo.blueprint.demo);
        this.isServices = this.serviceInfos.length > 0 ? true : false;
        this.serviceInfos.forEach((card: any) => {
          if (!card.serviceVersionsInfo?.devVersion) {
            if(this.servicesVersionsMap.get(card.blueprint.solutionId)) {
              card.buildNumber = this.servicesVersionsMap.get(card.blueprint.solutionId)[0]?.version;
            }
          }
        });

        this.serviceCardService.setInstanceData(this.serviceInfos);

          this.isSandbox = this.sandboxInfos.length > 0 ? true : false;
          if(this.isServices || this.isSandbox || this.serviceInfos){
            this.isLoader = false;
          }
      }
      });
    }
  }

  private defaultComparator(config1: any, config2: any): number {
  if(config1?.blueprint) {
    if (config1?.blueprint.dateCreated && config2?.blueprint.dateCreated) {
      return new Date(config2.blueprint.dateCreated).getTime() - new Date(config1.blueprint.dateCreated).getTime();
    }
    return 0;
    } else {
      if (config1?.dateCreated && config2?.dateCreated) {
        return new Date(config2.dateCreated).getTime() - new Date(config1.dateCreated).getTime();
      }
      return 0;
    }
  }
}
